import React, { useState, useRef, useCallback } from 'react'
import moment from 'moment'
import * as Sentry from '@sentry/browser'
import Webcam from 'react-webcam'
import { CircularProgress } from '@mui/material'
import { AnimatedCheck, CountdownTimer } from 'components/Antifraude'
import { useSelector, useDispatch } from 'react-redux'
import { post, URL_VARIABLES } from 'services/api'
import { verifyTokenRole } from 'config/tokenRouter'
import { getFullFaceDescription } from 'services/face'
import { setToken } from 'redux/reducers/token'
import { toast } from 'react-toastify'
import Countdown from './Countdown'
import { EllipseMask } from './Ellipse'

import { CameraContainer } from './styles'

export default (props) => {
  const { history } = props
  const webcamRef = useRef()
  const canvasRef = useRef(null)
  const [cameraStarted, setCameraStarted] = useState(false)
  const { token } = useSelector((state) => state.token)
  const [success, setSuccess] = useState(false)
  const [callbackExecuted, setCallbackExecuted] = useState(false)
  const [livenessSent, setLivenessSent] = useState(false)
  const [detectMessage, setDetectMessage] = useState('')
  const [validFace, setValidFace] = useState(false)
  const [sending, setSending] = useState(false)
  const [showCounter, setShowCounter] = useState(false)
  const [endedCapture, setEndedCapture] = useState(false)
  const { innerWidth: width } = window
  const dispatch = useDispatch()
  // let retry = 0
  const countAttempts = useRef(0) 
  
  const getBase64 = (file, cb) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const { result } = reader
      const parts = result.split(',')
      cb(parts[parts.length - 1])
    }
    reader.onerror = (error) => {
      return error
    }
  }

  function sizeOf(base64) {
    try {
      const base64str = base64.split('base64,')[1]
      const decoded = atob(base64str)
      return decoded.length
    } catch {
      return -1
    }
  }

  const handleResponseError = useCallback(
    (file_base64) => {
      Sentry.captureException(new Error('Erro ao enviar selfie'), (scope) => {
        scope.clear()
        scope.setTag('step', 'liveness-upload')
        scope.setContext('context', {
          info: file_base64 && file_base64.substr(0, 30),
          bytes: sizeOf(file_base64),
        })
        return scope
      })

      history.replace({ pathname: '/liveness-fail' })
    },
    [history],
  )

  const uploadFrame = useCallback(async (base64_file, userToken) => {
      if (!livenessSent) {
        setSending(true)

        post({
          url: '/onboarding/liveness',
          token: userToken,
          data: {
            base64: base64_file,
            count_attempts: countAttempts.current++,
          },
          source: URL_VARIABLES.ANTIFRAUDE,
        })
          .then((response) => {
            const { data } = response
            setSuccess(true)
            setTimeout(() => {
              dispatch(setToken(data.token))
              verifyTokenRole(data.token, history, data.redirect_url)
            }, 2000)
          })
          .catch(() => {
            handleResponseError(base64_file)
          })
          .finally(() => {
            setSending(false)
          })
      }
      setLivenessSent(true)
    },
    [countAttempts, dispatch, handleResponseError, history, livenessSent],
  )

  const sendFrame = useCallback(async (screenshot) => {
    await fetch(screenshot)
      .then((res) => res.blob())
      .then((blob) => {
        getBase64(blob, (base64_file) => {
          return uploadFrame(base64_file, token)
        })
      })
  }, [token, uploadFrame])
  

  const main = useCallback(async () => {
    const screenshot = webcamRef.current?.getScreenshot()

    if (screenshot) {      await getFullFaceDescription(webcamRef.current.getScreenshot(), token).then(
        async (fullDesc) => {
          const minWidth = (width > 600 ? 600 : width) * 0.82 // percentual mínimo do rosto na tela (não é o percentual do rosto na câmera, pois a imagem é obtida de um screenshot)
          const maxWidth = (width > 600 ? 600 : width) * 0.97 // percentual máximo do rosto na tela (não é o percentual do rosto na câmera, pois a imagem é obtida de um screenshot)

          if (fullDesc) {
            const face_box = fullDesc._box
            const faceWidth = face_box._width
            if (faceWidth < minWidth) {
              setValidFace(false)
              setDetectMessage('Aproxime seu rosto')
              setTimeout(main, 150)
            } else if (faceWidth > maxWidth) {
              setValidFace(false)
              setDetectMessage('Afaste seu rosto')
              setTimeout(main, 150)
            } else {
              setDetectMessage('Mantenha o rosto na posição atual')
              setShowCounter(true)
              setTimeout(async () => {
                const fallbackScreenshot = webcamRef.current?.getScreenshot()

                if (!fallbackScreenshot) {
                  Sentry.captureMessage("Liveness: Initial fallback screenshot was null. Cannot proceed.")
                  history.replace({ pathname: '/liveness-fail' })
                  return
                }

                setDetectMessage('Capturando imagem...')
                setShowCounter(false)
                setValidFace(true)

                let successfulFrame = null
                const MAX_ATTEMPTS = 5

                for (let i = 0; i < MAX_ATTEMPTS; i++) {
                  const currentScreenshot = webcamRef.current?.getScreenshot()

                  if (currentScreenshot) {
                    // eslint-disable-next-line no-await-in-loop
                    const descFound = await getFullFaceDescription(currentScreenshot, token, true)
                    if (descFound) {
                      successfulFrame = currentScreenshot
                      break
                    }
                  }
                  // eslint-disable-next-line no-await-in-loop
                  await new Promise(resolve => setTimeout(resolve, 100))
                }

                if (successfulFrame) {
                  await sendFrame(successfulFrame)
                } else {
                  Sentry.captureMessage("Liveness: Failed to find a perfect frame after 5 attempts. Sending initial fallback image.")
                  await sendFrame(fallbackScreenshot)
                }

                setCameraStarted(false)
                setEndedCapture(true)
              }, 3000)
            }
          } else {
            setValidFace(false)
            setDetectMessage('Encaixe seu rosto na moldura')
            setTimeout(main, 300)
          }
        },
      )
    } else {
      setValidFace(false)
      setDetectMessage('Encaixe seu rosto na moldura')
      setTimeout(main, 300)
    }

  }, [token, width, showCounter])

  const handleCameraStart = () => {
    setCameraStarted(true)
    main()
  }

  const handleCameraError = () => {
    toast.info(
      'O seu dispositivo não permite acesso a camera, favor autorizar e ou verificar se alguma camera está conectada',
    )
  }

  const handleTimeout = async () => {
    if (!callbackExecuted && !sending) {
      setCallbackExecuted(true)
      verifyTokenRole(token, history)
    }
    return null
  }

  return (
    <CameraContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div>
        <div className="hidden-counter">
          <CountdownTimer
            time={moment().add(30, 's')}
            callback={handleTimeout}
          />
        </div>
        <div className="camera">
          <Webcam
            audio={false}
            ref={webcamRef}
            minScreenshotHeight={700}
            screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: 'user' }}
            onUserMedia={handleCameraStart}
            onUserMediaError={handleCameraError}
          />
        </div>
        <canvas ref={canvasRef} id="canvas"></canvas>

        {showCounter && <Countdown seconds={3} />}

        {cameraStarted && (
          <React.Fragment>
            <div className={`cameraOverlay ${!detectMessage && 'off'}`}>
              <span className="statusMessage">{detectMessage}</span>
            </div>
            <EllipseMask loading={cameraStarted} success={validFace} />
          </React.Fragment>
        )}

        {!cameraStarted && endedCapture && (
          <div className="loading">
            <div className="loading__container">
              <span>
                {!success ? (
                  <div>
                    <CircularProgress size={60} />
                  </div>
                ) : (
                  <AnimatedCheck />
                )}
              </span>
              <span>
                {!success
                  ? 'Aguarde, processando imagem... Isso pode levar algum tempo.'
                  : 'Processamento concluído'}
              </span>
            </div>
          </div>
        )}
      </div>
    </CameraContainer>
  )
}
